import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, first, startWith, switchMap, tap } from 'rxjs/operators';
import {
  ApplicationViewModel,
  CloneDraftPipeClassRequest,
  EsraPipingAPIClient,
  PipeClassNameAvailabilityViewModel,
  ValveReleaseLookupViewModel,
} from 'src/app/shared/models/autogenerated-piping';
import { indicate } from 'src/app/operators';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';

/** Data passed into the ClonePipeClassDialogComponent. */
export interface ClonePipeClassDialogData {
  /** The ID of the pipe class to clone. */
  sourcePipeClassId: string;
  /**
   * Optional pre-fill value for the Business Unit dropdown.
   * Taken from the current revision context (e.g. revision dashboard BU).
   */
  prefillBusinessUnit?: string;
  /**
   * Optional pre-fill value for the Application dropdown.
   * Taken from the current revision context (e.g. revision dashboard application).
   */
  prefillApplication?: string;
  /**
   * When true, shows the informational message:
   * "This PipeClass will be cloned as a Draft. It will not be added to the current Release or Revision."
   * Intended for the Revision Dashboard entry point.
   */
  showDraftInfoMessage?: boolean;
  /**
   * Optional URL to navigate back to after a successful clone. When provided it is
   * passed as `state.returnUrl` to the router so `piping-tabs-parent` can restore the
   * correct back-button destination. Leave undefined when the caller does not need a
   * specific return destination (e.g. opening from the admin search panel).
   */
  returnUrl?: string;
}

@Component({
  selector: 'app-clone-pipe-class-dialog',
  templateUrl: './clone-pipe-class-dialog.component.html',
  styleUrls: ['./clone-pipe-class-dialog.component.scss'],
  standalone: false,
})
export class ClonePipeClassDialogComponent implements OnInit, OnDestroy {

  cloneForm: FormGroup;
  submitting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Inline error shown when the API returns 400 or 409. */
  cloneError: string | null = null;

  businessUnitOptions: string[] = [];
  applicationOptions: string[] = [];

  // Release dropdown state
  /** Available PUBLISHED pipe releases for the selected BU + Application. */
  releaseOptions: ValveReleaseLookupViewModel[] = [];
  /** True while the releases endpoint is in-flight. */
  isLoadingReleases$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True when releases were loaded but none are available for the chosen BU + Application. */
  noReleasesAvailable: boolean = false;

  // Name availability check state
  /** True while the name availability check call is in-flight. */
  isCheckingName$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** Set when the name already exists in the same BU+App — blocks the submit. */
  nameExistsError: boolean = false;
  /** Set when the name exists in a different BU — warning only, submit still allowed. */
  nameExistsInOtherBuWarning: boolean = false;

  private readonly subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<ClonePipeClassDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClonePipeClassDialogData,
    private readonly esraPipingApiClient: EsraPipingAPIClient,
    private readonly router: Router,
    private readonly snackBarService: SnackBarService
  ) {
    this.cloneForm = new FormGroup({
      pipeClassName:  new FormControl('',                            [Validators.required]),
      businessUnit:   new FormControl(data.prefillBusinessUnit ?? '', [Validators.required]),
      application:    new FormControl(data.prefillApplication  ?? '', [Validators.required]),
      releaseId:      new FormControl('',                            [Validators.required]),
    });
  }

  get pipeClassNameControl(): FormControl {
    return this.cloneForm.get('pipeClassName') as FormControl;
  }

  get businessUnitControl(): FormControl {
    return this.cloneForm.get('businessUnit') as FormControl;
  }

  get applicationControl(): FormControl {
    return this.cloneForm.get('application') as FormControl;
  }

  get releaseIdControl(): FormControl {
    return this.cloneForm.get('releaseId') as FormControl;
  }

  ngOnInit(): void {
    this.loadDropdownOptions();
    this.setupReleasesLoader();
    this.setupNameAvailabilityCheck();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Loads the Business Unit and Application dropdown options from the API.
   * Business units come from the shared endpoint used across all piping dialogs.
   * Applications come from the active application catalog.
   */
  private loadDropdownOptions(): void {
    this.subscription.add(
      this.esraPipingApiClient.getAllBusinessUnits()
        .pipe(first())
        .subscribe({
          next: (units: string[]) => {
            this.businessUnitOptions = units || [];
          }
        })
    );

    this.subscription.add(
      this.esraPipingApiClient.getAllCurrentApplications()
        .pipe(first())
        .subscribe({
          next: (apps: ApplicationViewModel[]) => {
            // Extract the display name from each ApplicationViewModel.
            this.applicationOptions = (apps || [])
              .filter(a => a.name)
              .map(a => a.name as string);
          }
        })
    );
  }

  /**
   * Subscribes to BU + Application value changes and reloads the available PUBLISHED
   * pipe releases whenever either changes. Resets `releaseId` on every reload so an
   * obsolete ID from a previous combination is never submitted.
   */
  private setupReleasesLoader(): void {
    this.subscription.add(
      combineLatest([
        this.businessUnitControl.valueChanges.pipe(startWith(this.businessUnitControl.value as string)),
        this.applicationControl.valueChanges.pipe(startWith(this.applicationControl.value as string)),
      ]).pipe(
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1])
      ).subscribe(([bu, app]: [string, string]) => {
        this.releaseIdControl.setValue('');
        this.releaseOptions = [];
        this.noReleasesAvailable = false;

        if (bu && app) {
          this.subscription.add(
            this.esraPipingApiClient.getPipeClassReleasesForCreation(bu, app)
              .pipe(indicate(this.isLoadingReleases$), first(), catchError(() => of([])))
              .subscribe((releases: ValveReleaseLookupViewModel[]) => {
                this.releaseOptions = releases ?? [];
                this.noReleasesAvailable = this.releaseOptions.length === 0;
                if (this.releaseOptions.length === 1) {
                  this.releaseIdControl.setValue(this.releaseOptions[0].id);
                }
              })
          );
        }
      })
    );
  }

  /**
   * Debounced check for name availability. Triggered whenever the user changes the
   * pipe class name, BU, or application. Sets `nameExistsError` (blocks submit) when
   * the name exists in the same BU+App, and `nameExistsInOtherBuWarning` (shows
   * warning only) when it exists in a different BU.
   */
  private setupNameAvailabilityCheck(): void {
    this.subscription.add(
      combineLatest([
        this.pipeClassNameControl.valueChanges.pipe(startWith(this.pipeClassNameControl.value as string)),
        this.businessUnitControl.valueChanges.pipe(startWith(this.businessUnitControl.value as string)),
        this.applicationControl.valueChanges.pipe(startWith(this.applicationControl.value as string)),
      ]).pipe(
        debounceTime(500),
        tap(() => {
          this.nameExistsError = false;
          this.nameExistsInOtherBuWarning = false;
        }),
        switchMap(([name, bu, app]: [string, string, string]) => {
          const trimmedName: string = name?.trim() ?? '';
          if (!trimmedName || !bu || !app) {
            this.isCheckingName$.next(false);
            return of(null);
          }
          this.isCheckingName$.next(true);
          return this.esraPipingApiClient.checkPipeClassNameAvailability(trimmedName, bu, app)
            .pipe(catchError(() => of(null)));
        })
      ).subscribe((result: PipeClassNameAvailabilityViewModel | null) => {
        this.isCheckingName$.next(false);
        if (!result) { return; }
        this.nameExistsError = result.existsInSameBusinessUnit === true;
        this.nameExistsInOtherBuWarning = !this.nameExistsError && result.existsInDifferentBusinessUnit === true;
      })
    );
  }

  isFormValid(): boolean {
    return this.cloneForm.valid && !this.submitting$.getValue() && !this.nameExistsError && !this.noReleasesAvailable;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onClone(): void {
    if (!this.isFormValid()) { return; }

    this.cloneError = null;
    this.submitting$.next(true);

    const request: CloneDraftPipeClassRequest = new CloneDraftPipeClassRequest({
      sourcePipeClassId:   this.data.sourcePipeClassId,
      newPipeClassName:    this.pipeClassNameControl.value?.trim(),
      targetBusinessUnit:  this.businessUnitControl.value,
      targetApplication:   this.applicationControl.value,
      releaseId:           this.releaseIdControl.value,
    });

    this.subscription.add(
      this.esraPipingApiClient.cloneDraftPipeClass(request)
        .pipe(first())
        .subscribe({
          next: (newPipeClassId: string) => {
            this.submitting$.next(false);
            this.snackBarService.showInfo('PipeClass cloned successfully as a new Draft.');
            this.dialogRef.close();
            // Navigate to the newly created Draft PipeClass detail page.
            // Pass returnUrl in router state so the back button lands on the correct screen.
            this.router.navigate(['/pipingsummary', newPipeClassId], {
              state: this.data.returnUrl ? { returnUrl: this.data.returnUrl } : undefined
            });
          },
          error: (err: unknown) => {
            this.submitting$.next(false);
            const httpError = err as Record<string, unknown>;

            if (httpError?.status === 409) {
              this.cloneError = 'A PipeClass with this name already exists in the selected Business Unit.';
            } else if (httpError?.status === 400) {
              this.cloneError = getApiErrorMessage(err, 'The request was not valid. Please check the entered values and try again.');
            } else {
              this.cloneError = 'An unexpected error occurred while cloning. Please try again.';
            }
          }
        })
    );
  }
}
